import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import AutocompleteInput from "@/components/AutocompleteInput";
import EnhancedPhotoUploader from "@/components/EnhancedPhotoUploader";

function FormField({ label, value, onChange, type = "text", placeholder = "", className = "", as = "input" }) {
  const Component = as === "textarea" ? Textarea : Input;
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</Label>
      <Component type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}

const DAMAGE_TYPES = [
  { value: "wet_damage", label: "Wet damage" },
  { value: "torn_packaging", label: "Torn packaging" },
  { value: "crushed_cargo", label: "Crushed cargo" },
  { value: "missing_cargo", label: "Missing cargo" },
  { value: "broken_pallet", label: "Broken pallet" },
  { value: "contamination", label: "Contamination" },
  { value: "temperature_issue", label: "Temperature issue" },
  { value: "seal_issue", label: "Seal issue" },
  { value: "other", label: "Other" },
];

const SEVERITY = [
  { value: "minor", label: "Minor" },
  { value: "moderate", label: "Moderate" },
  { value: "major", label: "Major" },
  { value: "total_loss", label: "Total loss" },
];

const PHOTO_CATEGORIES = [
  "General view", "Close-up damage", "Packaging damage", "Label / marking",
  "Container / trailer condition", "Seal", "Corrective action", "Other"
];

export default function DamagedShipmentForm({ data, onChange }) {
  return (
    <div className="space-y-6" data-testid="damaged-shipment-form">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Header Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AutocompleteInput label="Applicant / Customer" value={data.applicant} onChange={v => onChange("applicant", v)} endpoint="applicants" testId="applicant-input" placeholder="Start typing..." />
          <FormField label="Our Reference Number" value={data.reference_number} onChange={v => onChange("reference_number", v)} />
          <FormField label="Customer Reference Number" value={data.customer_reference_number} onChange={v => onChange("customer_reference_number", v)} />
          <FormField label="Date of Inspection" value={data.date_of_inspection} onChange={v => onChange("date_of_inspection", v)} type="date" />
          <FormField label="Place of Inspection" value={data.place_of_inspection} onChange={v => onChange("place_of_inspection", v)} />
          <AutocompleteInput label="Surveyor" value={data.surveyor} onChange={v => onChange("surveyor", v)} endpoint="surveyors" testId="surveyor-input" placeholder="Start typing..." />
        </div>
      </div>

      {/* Shipment Details */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Shipment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Shipper" value={data.shipper} onChange={v => onChange("shipper", v)} />
          <FormField label="Consignee / Notify Party" value={data.consignee} onChange={v => onChange("consignee", v)} />
          <FormField label="Port of Loading" value={data.port_of_loading} onChange={v => onChange("port_of_loading", v)} />
          <FormField label="Place of Delivery" value={data.place_of_delivery} onChange={v => onChange("place_of_delivery", v)} />
          <FormField label="Carrier" value={data.carrier} onChange={v => onChange("carrier", v)} />
          <FormField label="Vessel / Truck / Rail Details" value={data.vessel_details} onChange={v => onChange("vessel_details", v)} />
          <FormField label="Container Number" value={data.container_number} onChange={v => onChange("container_number", v)} />
          <FormField label="Seal Number" value={data.seal_number} onChange={v => onChange("seal_number", v)} />
          <FormField label="B/L / CMR / AWB Number" value={data.bill_of_lading_number} onChange={v => onChange("bill_of_lading_number", v)} />
          <FormField label="Commodity Description" value={data.commodity_description} onChange={v => onChange("commodity_description", v)} />
          <FormField label="Total Gross Weight" value={data.total_gross_weight} onChange={v => onChange("total_gross_weight", v)} />
          <FormField label="Number of Packages / Pallets" value={data.number_of_packages} onChange={v => onChange("number_of_packages", v)} />
        </div>
      </div>

      {/* Damage Details */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Damage Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Date Damage Noticed" value={data.date_damage_noticed} onChange={v => onChange("date_damage_noticed", v)} type="date" />
          <FormField label="Location Damage Noticed" value={data.location_damage_noticed} onChange={v => onChange("location_damage_noticed", v)} />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Damage Type</Label>
            <Select value={data.damage_type || ""} onValueChange={v => onChange("damage_type", v)}>
              <SelectTrigger className="border-slate-300" data-testid="damage-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {DAMAGE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Damage Severity</Label>
            <Select value={data.damage_severity || ""} onValueChange={v => onChange("damage_severity", v)}>
              <SelectTrigger className="border-slate-300" data-testid="damage-severity-select"><SelectValue placeholder="Select severity" /></SelectTrigger>
              <SelectContent>
                {SEVERITY.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FormField label="Description of Damage" value={data.description_of_damage} onChange={v => onChange("description_of_damage", v)} as="textarea" className="md:col-span-2" />
          <FormField label="Suspected Cause" value={data.suspected_cause} onChange={v => onChange("suspected_cause", v)} as="textarea" className="md:col-span-2" />
          <FormField label="Immediate Action Taken" value={data.immediate_action_taken} onChange={v => onChange("immediate_action_taken", v)} as="textarea" className="md:col-span-2" />
          <div className="flex items-center gap-3">
            <Switch checked={data.customer_notified || false} onCheckedChange={v => onChange("customer_notified", v)} data-testid="customer-notified-switch" />
            <Label className="text-sm text-slate-700">Customer / Carrier Notified</Label>
          </div>
          <FormField label="Claim Reference Number" value={data.claim_reference_number} onChange={v => onChange("claim_reference_number", v)} />
        </div>
      </div>

      {/* Photo Evidence */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Photo Evidence</h3>
        <EnhancedPhotoUploader
          entries={data.photo_entries || []}
          onChange={v => onChange("photo_entries", v)}
          categories={PHOTO_CATEGORIES}
        />
      </div>

      {/* Findings */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings & Observations</h3>
        <div className="space-y-4">
          <FormField label="Survey Findings" value={data.survey_findings} onChange={v => onChange("survey_findings", v)} as="textarea" />
          <FormField label="Observations" value={data.observations} onChange={v => onChange("observations", v)} as="textarea" />
          <FormField label="Recommendation / Next Action" value={data.recommendation} onChange={v => onChange("recommendation", v)} as="textarea" />
          <FormField label="Disclaimer" value={data.disclaimer} onChange={v => onChange("disclaimer", v)} as="textarea" />
        </div>
      </div>
    </div>
  );
}
